import { resolveNoContent, resolveWithBody } from "./Utils/utils";
import { Schedule } from "../Common/Schedule/types";
import { usePromisedRequest as promiseRequest } from "../Common/request";
import { BracketSchedulingConfig } from "../Common/Brackets/types";
import { RoundMarker } from "../Schedules/Managing/dropZoneItems";

/**
 * See: \Joanna\Controller\Api\Schedule\ScheduleController::getListing
 */
export type ActiveScheduleResponse = {
    active_schedule: Schedule;
    all_schedules: Array<Schedule>;
};

/**
 * Assignment type value constants from backend:
 * - FullAssignmentType::FULL = 1
 * - HalfAssignmentType::FIRST_HALF = 2
 * - HalfAssignmentType::SECOND_HALF = 4
 * - QuarterAssignmentType::FIRST_QUARTER = 8
 * - QuarterAssignmentType::SECOND_QUARTER = 16
 * - QuarterAssignmentType::THIRD_QUARTER = 32
 * - QuarterAssignmentType::FOURTH_QUARTER = 64
 */
export type AssignmentTypeValue = 1 | 2 | 4 | 8 | 16 | 32 | 64;

export interface AssignmentType {
    value: AssignmentTypeValue;
    representation: string;
}

export type PublicScheduledCategoryTime = { start: string; end: string };

export type PublicScheduleCategory = {
    id: number;
    name: string;
    scheduledCategoryTime: PublicScheduledCategoryTime;
    assignmentType: AssignmentType;
    roundMarkerFrom: string | null;
    roundMarkerTo: string | null;
    totalFights: number;
    doneFights: number;
};

export type PublicScheduleMat = { id: number; name: string; categories: PublicScheduleCategory[] };

export type PublicSchedule = { id: number; name: string; sharing: number; mats: PublicScheduleMat[] };

export type PublicSchedulesResponse = {
    activeScheduleId: number | null;
    schedules: PublicSchedule[];
};

export type BasicBracketJsonViewForSchedule = {
    totalRealCompetitors: number;
};

export type NamedScheduledCategory = {
    id: number;
    parameterId: number;
    category: string;
    assignmentType: AssignmentType;
    bracket: BasicBracketJsonViewForSchedule;
    canBeMoved: boolean;
    roundMarkerFrom: string | null;
    roundMarkerTo: string | null;
};

export type DropZoneItemResponse = {
    key: string;
    parameterId: number;
    name: string;
    totalRealCompetitors: number;
    assignmentType: AssignmentTypeValue;
    roundMarkerFrom: string | null;
    roundMarkerTo: string | null;
    segmentIndex: number;
    partIndex: number;
    partsCount: 1 | 2 | 4;
    segment: {
        from: string | null;
        to: string | null;
        equalParts: 1 | 2 | 4;
        isRest: boolean;
        includesThirdPlace: boolean;
    };
    bracketType: number;
    hasSchedulingConfig: boolean;
    schedulingConfig: { isolatedRounds: string[]; equalParts: Record<string, number> } | null;
    supportsRoundSlicing: boolean;
};

export type MatScheduleSummary = {
    totalFights: number;
    totalTime: number;
    eta: string | null;
};

export type MatSchedule = {
    id: number;
    name: string;
    scheduledCategories: NamedScheduledCategory[];
};

export type ScheduledCategoryTime = {
    start: string;
    end: string;
};

export interface MatSchedulesResult {
    matSchedules: Record<number, MatSchedule>;
    startTimes: Record<number, Record<number, ScheduledCategoryTime>>;
    conflicts: {
        scheduledCategoryIds: number[];
        present: boolean;
        mapping: Record<string, number[]>;
    };
    matScheduleSummaries: Record<number, MatScheduleSummary>;
}

export type SingleConflictDetails = {
    matName: string;
    categoryName: string;
};

export type ConflictDetailsResult = {
    competitor: {
        competitorId: number;
        firstName: string;
        lastName: string;
        nationality: string;
    };
    conflictDetails: SingleConflictDetails[];
}[];

export type ConflictsForScheduledCategory = {
    requestedScheduledCategory: {
        categoryName?: string;
        matId?: number;
        time?: ScheduledCategoryTime;
    };
    conflicts: {
        matName: string;
        categoryName: string;
        matId: number;
        time: ScheduledCategoryTime;
    }[];
};

export type AddingOrder = "asc" | "desc" | "selection";

export type BulkSaveScheduledCategoriesRequest = {
    eventId: number;
    scheduleId: number;
    matIds: number[];
    parameterIds: number[];
    assignmentType: AssignmentTypeValue;
    addingOrder: AddingOrder;
    targetPosition?: number | null;
    roundMarkerFrom?: string | null;
    roundMarkerTo?: string | null;
};

export type BulkDeleteScheduledCategoriesRequest = {
    scheduledCategoryIds: number[];
};

export type BracketSegment = {
    from: RoundMarker | null;
    to: RoundMarker | null;
    equalParts: number;
    isRest: boolean;
    includesThirdPlace: boolean;
};

export type PartiallyScheduledBracket = {
    parameterId: number;
    name: string;
    missingSegments: BracketSegment[];
};

export type SupportedSharingType = {
    key: string;
    value: number;
};

export default {
    getSupportedSharingTypes(eventId: number): Promise<SupportedSharingType[]> {
        return promiseRequest<SupportedSharingType[]>({
            url: `/api/admin/events/${eventId}/schedules/sharing`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getSchedules(eventId: number): Promise<ActiveScheduleResponse> {
        return promiseRequest<ActiveScheduleResponse>({
            url: `/api/admin/events/${eventId}/schedules`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    updateSchedule(eventId: number, scheduleObj) {
        return promiseRequest({
            method: "post",
            url: `/api/admin/events/${eventId}/schedules`,
            json: {
                scheduleId: scheduleObj.scheduleId,
                eventId: eventId,
                scheduleName: scheduleObj.scheduleName,
                startTime: scheduleObj.startTime,
                scheduleSharing: scheduleObj.scheduleSharing,
            },
        }).then(response => {
            if (response.statusCode !== 204) {
                throw Error("Could not update schedule.");
            }
        });
    },
    updateScheduleSharing(eventId, scheduleId, sharing, setAsPlanned) {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/schedules/${scheduleId}/sharing`,
            method: "put",
            json: {
                sharing: sharing,
                setAsPlanned: setAsPlanned,
            },
        }).then(resolveNoContent());
    },
    deleteSchedule(eventId: number, scheduleId: number): Promise<void> {
        return promiseRequest({
            method: "delete",
            url: `/api/admin/events/${eventId}/schedules/${scheduleId}`,
            json: true,
        }).then(resolveNoContent());
    },
    activateSchedule(eventId: number, scheduleId: number): Promise<void> {
        return promiseRequest({
            method: "post",
            url: `/api/admin/events/${eventId}/schedules/${scheduleId}/actions/activate`,
            json: true,
        }).then(resolveNoContent("Could not activate schedule."));
    },
    erasePlannedVersion(eventId, scheduleId) {
        return promiseRequest({
            method: "delete",
            url: `/api/admin/events/${eventId}/schedules/${scheduleId}/planned-version`,
            json: true,
        }).then(resolveNoContent());
    },

    getDropZoneItems(eventId: number, scheduleId: number) {
        return promiseRequest<DropZoneItemResponse[]>({
            url: `/api/admin/events/${eventId}/schedules/${scheduleId}/drop-zone-items`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    removeScheduledCategories(
        eventId: number,
        scheduleId: number,
        request: BulkDeleteScheduledCategoriesRequest,
        conflictsThreshold: number,
    ): Promise<MatSchedulesResult> {
        return promiseRequest<MatSchedulesResult>({
            method: "post",
            json: true,
            url: `/api/admin/events/${eventId}/schedules/${scheduleId}/categories/actions/bulk-delete?conflictsThreshold=${conflictsThreshold}`,
            body: request,
        }).then(resolveWithBody());
    },
    getPublicScheduleOfSpecificScheduleSharing(eventId, scheduleId, scheduleSharing) {
        let query = "";
        if (scheduleSharing) {
            query = new URLSearchParams({
                sharing: scheduleSharing,
            }).toString();
        }

        return promiseRequest({
            url: `/api/events/${eventId}/schedules/${scheduleId}?${query}`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getRawScheduleById(eventId: number, scheduleId: number): Promise<Schedule> {
        return promiseRequest<Schedule>({
            url: `/api/admin/events/${eventId}/schedules/${scheduleId}/raw`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getSchedulesPublicData(eventId: number, preferredScheduleSharing: Record<number, number | null>): Promise<PublicSchedulesResponse> {
        let query = "";

        if (!!preferredScheduleSharing) {
            query = new URLSearchParams({
                sharing: JSON.stringify(preferredScheduleSharing),
            }).toString();
        }

        return promiseRequest<PublicSchedulesResponse>({
            url: `/api/events/${eventId}/schedules?${query}`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    /**
     * Data param is:
     * [
     *     {matId: <number>, scheduledCategoryIds: int[] }
     * ]
     */
    bulkUpdateMatSchedules(
        eventId: number,
        scheduleId: number,
        data: Array<{ matId: number; scheduledCategoryIds: number[] }>,
        conflictsThreshold: number,
    ): Promise<MatSchedulesResult> {
        return promiseRequest<MatSchedulesResult>({
            url: `/api/admin/events/${eventId}/schedules/${scheduleId}/mats`,
            method: "PUT",
            json: {
                scheduleId: scheduleId,
                conflictsThreshold: conflictsThreshold,
                matScheduleUpdates: data,
            },
        }).then(resolveWithBody());
    },
    bulkSaveScheduledCategories(request: BulkSaveScheduledCategoriesRequest) {
        return promiseRequest({
            url: `/api/admin/events/${request.eventId}/schedules/${request.scheduleId}/categories`,
            method: "POST",
            json: request,
        }).then(resolveNoContent(undefined, true));
    },
    getAllMatSchedules(
        eventId: number,
        scheduleId: number,
        matIds: number[] = [],
        conflictsThreshold: number,
    ): Promise<MatSchedulesResult> {
        const queryParam = [matIds.length > 0 ? `matIds=${matIds.join(",")}` : "", `conflictsThreshold=${conflictsThreshold}`];

        const joined = queryParam.filter(param => param.length > 0).join("&");

        return promiseRequest<MatSchedulesResult>({
            url: `/api/admin/events/${eventId}/schedules/${scheduleId}/mats?${joined}`,
            method: "GET",
            json: true,
        }).then(resolveWithBody());
    },
    getConflictsForAllCompetitors(eventId: number, scheduleId: number, conflictsThreshold: number): Promise<ConflictDetailsResult> {
        return promiseRequest<ConflictDetailsResult>({
            url: `/api/admin/events/${eventId}/schedules/${scheduleId}/conflicts?conflictsThreshold=${conflictsThreshold}`,
            method: "GET",
            json: true,
        }).then(resolveWithBody());
    },
    getConflictsForScheduledCategory(
        eventId: number,
        scheduleId: number,
        scheduledCategoryId: number,
        conflictsThreshold: number,
    ): Promise<ConflictsForScheduledCategory> {
        return promiseRequest<ConflictsForScheduledCategory>({
            url: `/api/admin/events/${eventId}/schedules/${scheduleId}/categories/${scheduledCategoryId}/conflicts?conflictsThreshold=${conflictsThreshold}`,
            method: "GET",
            json: true,
        }).then(resolveWithBody());
    },
    fetchActiveSchedule(eventId: number): Promise<ActiveScheduleResponse> {
        return promiseRequest<ActiveScheduleResponse>({
            url: `/api/admin/events/${eventId}/schedules`,
            json: true,
            method: "get",
        }).then(resolveWithBody());
    },
    saveBracketSchedulingConfig(eventId: number, parameterId: number, config: BracketSchedulingConfig): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/brackets/${parameterId}/scheduling-config`,
            method: "PUT",
            json: config,
        }).then(resolveNoContent());
    },
    deleteBracketSchedulingConfig(eventId: number, parameterId: number): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/brackets/${parameterId}/scheduling-config`,
            method: "DELETE",
            json: true,
        }).then(resolveNoContent());
    },
};
